import guidesIndex from './guidesIndex.json';
import cityHeroes from './cityHeroes.json';
import cityStock from './cityStock.json';
import heroAttribution from './heroAttribution.json';
import COUNTRY_REGION_JSON from './regions.json';

// Lightweight index (bundled) powers the grid + sitemap.
export { guidesIndex };

// Hero image for a city: premium AI hero first, then free stock (Wikimedia) hero,
// else null -> gradient placeholder. Full-site coverage for affiliate review.
export function cityHero(slug) {
  return cityHeroes[slug] || cityStock[slug] || null;
}

// Attribution / licensing metadata for a city's hero image (data-driven).
// { type:'ai' } for AI-generated, or a photo object with sourceName/sourceUrl/
// authorName/authorUrl/licenseName/licenseUrl. Returns null if unknown.
export function cityHeroMeta(slug) {
  return heroAttribution[slug] || null;
}

// Country -> region grouping (single source of truth: regions.json).
export const COUNTRY_REGION = COUNTRY_REGION_JSON;

export const regionOf = (country) => COUNTRY_REGION[country] || 'Other';

// --- Region -> Country -> City hierarchy (browse IA + crawlable hub pages) ---
export const REGION_ORDER = ['Europe', 'North America', 'Latin America', 'Other'];
export const REGION_SLUG = {
  Europe: 'europe',
  'North America': 'north-america',
  'Latin America': 'latin-america',
  Other: 'other',
};

const slugify = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
export const countrySlug = (c) => slugify(c);
export const regionSlug = (r) => REGION_SLUG[r] || slugify(r);
export const regionFromSlug = (s) =>
  Object.keys(REGION_SLUG).find((r) => REGION_SLUG[r] === s) || null;

// Builds the ordered Region -> Country -> City tree from the bundled index.
let _treeCache = null;
export function guidesTree() {
  if (_treeCache) return _treeCache;
  const map = {};
  guidesIndex.forEach((g) => {
    const region = regionOf(g.country);
    (map[region] = map[region] || {});
    (map[region][g.country] = map[region][g.country] || []).push(g);
  });
  const tree = REGION_ORDER.filter((r) => map[r]).map((region) => {
    const countries = Object.keys(map[region])
      .sort((a, b) => a.localeCompare(b))
      .map((country) => ({
        country,
        countrySlug: countrySlug(country),
        cities: map[region][country].slice().sort((a, b) => a.cityName.localeCompare(b.cityName)),
      }));
    return {
      region,
      regionSlug: regionSlug(region),
      countries,
      count: countries.reduce((n, c) => n + c.cities.length, 0),
    };
  });
  _treeCache = tree;
  return tree;
}

// Lookup helpers for hub pages.
export function regionNode(rSlug) {
  return guidesTree().find((n) => n.regionSlug === rSlug) || null;
}
export function countryNode(rSlug, cSlug) {
  const r = regionNode(rSlug);
  if (!r) return null;
  const country = r.countries.find((c) => c.countrySlug === cSlug) || null;
  return country ? { region: r, country } : null;
}

// North America has one country (USA); we present it as travel regions instead.
export const US_REGION_ORDER = ['Northeast U.S.', 'Southeast U.S.', 'Midwest U.S.', 'Southwest U.S.', 'Western U.S.'];
const US_REGION_BY_CITY = {
  'Boston': 'Northeast U.S.', 'New York City': 'Northeast U.S.', 'Philadelphia': 'Northeast U.S.', 'Washington, DC': 'Northeast U.S.',
  'Charleston': 'Southeast U.S.', 'Key West': 'Southeast U.S.', 'Miami': 'Southeast U.S.', 'Nashville': 'Southeast U.S.', 'New Orleans': 'Southeast U.S.', 'Orlando': 'Southeast U.S.', 'Savannah': 'Southeast U.S.',
  'Chicago': 'Midwest U.S.',
  'Austin': 'Southwest U.S.', 'Grand Canyon': 'Southwest U.S.', 'Las Vegas': 'Southwest U.S.',
  'Honolulu': 'Western U.S.', 'Los Angeles': 'Western U.S.', 'Maui': 'Western U.S.', 'San Diego': 'Western U.S.', 'San Francisco': 'Western U.S.', 'Seattle': 'Western U.S.', 'Yosemite National Park': 'Western U.S.', 'Yellowstone': 'Western U.S.',
};

// USA cities grouped into travel regions, shaped like country nodes (country/countrySlug/cities).
export function naRegionGroups() {
  const us = guidesIndex.filter((g) => g.country === 'United States');
  return US_REGION_ORDER
    .map((r) => ({
      country: r,
      countrySlug: slugify(r),
      cities: us.filter((g) => US_REGION_BY_CITY[g.cityName] === r).slice().sort((a, b) => a.cityName.localeCompare(b.cityName)),
    }))
    .filter((g) => g.cities.length);
}
export function naRegionNode(cSlug) {
  return naRegionGroups().find((g) => g.countrySlug === cSlug) || null;
}


// Full guide JSON is code-split: webpack creates one chunk per guide.
export async function loadGuide(slug) {
  try {
    const mod = await import(`./guides/${slug}.json`);
    return mod.default || mod;
  } catch (e) {
    return null;
  }
}

// Deterministic premium gradient placeholder per city (until AI heroes are generated).
export function cityGradient(slug) {
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) % 360;
  const h2 = (h + 40) % 360;
  return `linear-gradient(135deg, hsl(${h} 45% 32%) 0%, hsl(${h2} 50% 22%) 100%)`;
}
